export interface PhoneObject {
  countryCode: string
  areaCode: string
  phoneNumber: string
}

export interface PhoneFields {
  ddi?: string
  ddd?: string
  telefone?: string
  cliente_ddi?: string
  cliente_ddd?: string
  cliente_telefone?: string
}

interface CountryConfig {
  code: string
  areaCodeLength: number
}

export function phoneToString(phoneObj: PhoneObject | null): string {
  if (!phoneObj || !phoneObj.phoneNumber) {
    return ''
  }

  const countryCode = phoneObj.countryCode || ''
  const areaCode = phoneObj.areaCode || ''
  const phoneNumber = phoneObj.phoneNumber || ''

  return `${countryCode}${areaCode}${phoneNumber}`
}

export function phoneFromString(phoneStr: string | null): PhoneObject {
  const emptyPhone: PhoneObject = { countryCode: '+55', areaCode: '', phoneNumber: '' }

  if (!phoneStr || typeof phoneStr !== 'string') {
    return emptyPhone
  }

  if (!phoneStr.startsWith('+')) {
    return emptyPhone
  }

  const countryConfigs: CountryConfig[] = [
    { code: '+351', areaCodeLength: 0 },
    { code: '+55', areaCodeLength: 2 },
    { code: '+56', areaCodeLength: 0 },
    { code: '+52', areaCodeLength: 0 },
    { code: '+54', areaCodeLength: 0 },
    { code: '+49', areaCodeLength: 0 },
    { code: '+44', areaCodeLength: 0 },
    { code: '+39', areaCodeLength: 0 },
    { code: '+34', areaCodeLength: 0 },
    { code: '+33', areaCodeLength: 0 },
    { code: '+1', areaCodeLength: 3 },
    { code: '+502', areaCodeLength: 0 },
    { code: '+591', areaCodeLength: 0 },
    { code: '+593', areaCodeLength: 0 },
    { code: '+595', areaCodeLength: 0 },
    { code: '+598', areaCodeLength: 0 },
    { code: '+244', areaCodeLength: 0 }
  ]

  let matchedConfig: CountryConfig | null = null
  for (const config of countryConfigs) {
    if (phoneStr.startsWith(config.code)) {
      matchedConfig = config
      break
    }
  }

  if (!matchedConfig) {
    return emptyPhone
  }

  const countryCode = matchedConfig.code
  const areaCodeLength = matchedConfig.areaCodeLength
  const rest = phoneStr.substring(countryCode.length)

  const areaCode = areaCodeLength > 0 ? rest.substring(0, areaCodeLength) : ''
  const phoneNumber = rest.substring(areaCodeLength)

  return {
    countryCode,
    areaCode,
    phoneNumber
  }
}

export function phoneToFields(phoneObj: PhoneObject | null): { ddi: string; ddd: string; telefone: string } {
  if (!phoneObj) {
    return { ddi: '', ddd: '', telefone: '' }
  }

  return {
    ddi: phoneObj.countryCode || '',
    ddd: phoneObj.areaCode || '',
    telefone: phoneObj.phoneNumber || ''
  }
}

export function phoneFromFields(fields: PhoneFields | null): PhoneObject {
  if (!fields) {
    return { countryCode: '+55', areaCode: '', phoneNumber: '' }
  }

  return {
    countryCode: fields.ddi || fields.cliente_ddi || '+55',
    areaCode: fields.ddd || fields.cliente_ddd || '',
    phoneNumber: fields.telefone || fields.cliente_telefone || ''
  }
}

/** Tamanho máximo de um número internacional (E.164), com o código do país. */
const MAX_DIGITOS_E164 = 15

/**
 * O número completo para o link do WhatsApp (só dígitos: país + área + número), ou null quando
 * o cadastro não dá para completar (chamado 2026-0053).
 *
 * As telas montavam o link cada uma por si e só mostravam o botão quando havia DDD — mas DDD
 * é coisa de Brasil (e de +1): Espanha, Portugal e os demais países de `phoneFromString` gravam
 * a área VAZIA, e o cliente de Valladolid ficava sem botão. A mesma regra do backend da
 * retomada (RetomadaTelefone.php): no Brasil o DDD é exigido, a menos que já venha junto do
 * número; nos outros países é país + área + número como está no cadastro, sem interpretar;
 * sem país no cadastro, Brasil.
 */
export function whatsappNumber(fields: PhoneFields | null | undefined): string | null {
  const digitos = (v: unknown): string => String(v ?? '').replace(/\D/g, '')
  let numero = digitos(fields?.telefone ?? fields?.cliente_telefone)
  if (numero === '') return null

  const pais = digitos(fields?.ddi ?? fields?.cliente_ddi) || '55'
  const area = digitos(fields?.ddd ?? fields?.cliente_ddd).replace(/^0+/, '')

  if (pais !== '55') {
    // Fora do Brasil a regra NÃO interpreta o número: é país + área + número, exatamente o link
    // que as telas já montavam — a única diferença é não exigir área. Nada de tirar zero nem de
    // adivinhar "já veio com o país": um número nacional pode começar com os dígitos do país.
    const inteiro = pais + digitos(fields?.ddd ?? fields?.cliente_ddd) + numero
    // O único corte: nenhum telefone do mundo passa de 15 dígitos (E.164). Mais que isso é
    // cadastro com lixo junto do número, e o link abriria a conversa de ninguém — ou de outro.
    return inteiro.length <= MAX_DIGITOS_E164 ? inteiro : null
  }

  // Brasil: número já inteiro (55 + DDD + número; um nacional tem no máximo 11 dígitos).
  if (numero.length >= 12 && numero.startsWith(pais)) return numero
  numero = numero.replace(/^0+/, '')
  // DDD junto do número (10 = fixo, 11 = celular).
  if (numero.length === 10 || numero.length === 11) return pais + numero
  // Só o número (8 = fixo ou celular antigo sem o 9, 9 = celular): precisa do DDD.
  if ((numero.length === 8 || numero.length === 9) && area.length === 2) return pais + area + numero
  return null
}

/** O link de conversa do WhatsApp para o cadastro, ou null (ver whatsappNumber). */
export function whatsappLink(fields: PhoneFields | null | undefined): string | null {
  const numero = whatsappNumber(fields)
  return numero === null ? null : `https://api.whatsapp.com/send?phone=${numero}`
}

export function isPhoneValid(phoneObj: PhoneObject | null): boolean {
  return phoneObj !== null && phoneObj !== undefined && phoneObj.phoneNumber !== undefined && phoneObj.phoneNumber.length > 0
}

export function getEmptyPhone(defaultCountryCode: string = '+55'): PhoneObject {
  return {
    countryCode: defaultCountryCode,
    areaCode: '',
    phoneNumber: ''
  }
}

export function usePhoneFormat() {
  return {
    phoneToString,
    phoneFromString,
    phoneToFields,
    phoneFromFields,
    isPhoneValid,
    getEmptyPhone
  }
}
